import { BaselineAudit_Entity } from '../baseline-audit';
import { ArrayTypeSchema, EnumBaselineProp, SchemaValue } from '../schema';
import { Baseline } from '../../../../stores/client/sway/baseline/model';
import { malwareFileTypesBestPractices } from './malware-file-types-filter-policy.schema';

export { malwareFileTypesBestPractices };

export interface AntiMalwarePolicy_SchemaData {
    ZapEnabled: SchemaValue<boolean, 'boolean'>;
    IsDefault: SchemaValue<boolean, 'boolean'>;
    Identity: SchemaValue<string>;
    EnableFileFilter: SchemaValue<boolean, 'boolean'>;
    FileTypes?: EnumBaselineProp;
    FileTypeAction?: SchemaValue<string>;
    QuarantineTag?: SchemaValue<string>;
    EnableInternalSenderAdminNotifications: SchemaValue<boolean, 'boolean'>;
    InternalSenderAdminAddress?: SchemaValue<string>;
    EnableExternalSenderAdminNotifications: SchemaValue<boolean, 'boolean'>;
    ExternalSenderAdminAddress?: SchemaValue<string>;
    CustomNotifications?: SchemaValue<boolean, 'boolean'>;
    CustomFromName?: SchemaValue<string>;
    CustomFromAddress?: SchemaValue<string>;
    CustomInternalSubject?: SchemaValue<string>;
    CustomInternalBody?: SchemaValue<string>;
    CustomExternalSubject?: SchemaValue<string>;
    CustomExternalBody?: SchemaValue<string>;
}

export type AntiMalwarePolicy_Schema = ArrayTypeSchema<AntiMalwarePolicy_SchemaData>;
export type AntiMalwarePolicy_Baseline = Baseline<AntiMalwarePolicy_SchemaData>;
export type AntiMalwarePolicy_Deviation = BaselineAudit_Entity<AntiMalwarePolicy_Schema>;

interface AntiMalwarePolicy_Form {
    ZapEnabled: boolean;
    EnableFileFilter: boolean;
    FileTypes: string[];
    FileTypeAction: string;
    QuarantineTag: string;
    EnableInternalSenderAdminNotifications: boolean;
    InternalSenderAdminAddress: string;
    EnableExternalSenderAdminNotifications: boolean;
    ExternalSenderAdminAddress: string;
    CustomNotifications: boolean;
    CustomFromName: string;
    CustomFromAddress: string;
    CustomInternalSubject: string;
    CustomInternalBody: string;
    CustomExternalSubject: string;
    CustomExternalBody: string;
}

export function generateAntiMawarePolicySchema(formValue: AntiMalwarePolicy_Form): AntiMalwarePolicy_Schema {
    const schema: AntiMalwarePolicy_Schema = {
        type: 'array',
        contains: {
            type: 'object',
            properties: {
                ZapEnabled: { type: 'boolean', const: formValue.ZapEnabled },
                IsDefault: { type: 'boolean', const: true },
                Identity: { type: 'string', const: 'Default' },
                EnableFileFilter: { type: 'boolean', const: formValue.EnableFileFilter },
                EnableInternalSenderAdminNotifications: {
                    type: 'boolean',
                    const: formValue.EnableInternalSenderAdminNotifications,
                },
                EnableExternalSenderAdminNotifications: {
                    type: 'boolean',
                    const: formValue.EnableExternalSenderAdminNotifications,
                },
                QuarantineTag: {
                    const: formValue.QuarantineTag,
                    type: 'string',
                },
                CustomNotifications: { type: 'boolean', const: formValue.CustomNotifications },
            },
            required: [
                'ZapEnabled',
                'IsDefault',
                'Identity',
                'EnableFileFilter',
                'EnableInternalSenderAdminNotifications',
                'EnableExternalSenderAdminNotifications',
                'QuarantineTag',
                'CustomNotifications',
            ],
        },
    };

    if (formValue.EnableInternalSenderAdminNotifications) {
        schema.contains.properties.InternalSenderAdminAddress = {
            type: 'string',
            const: formValue.InternalSenderAdminAddress?.toLocaleLowerCase()?.trim() || '',
        };
        schema.contains.required.push('InternalSenderAdminAddress');
    }

    if (formValue.EnableExternalSenderAdminNotifications) {
        schema.contains.properties.ExternalSenderAdminAddress = {
            type: 'string',
            const: formValue.ExternalSenderAdminAddress?.toLocaleLowerCase()?.trim() || '',
        };
        schema.contains.required.push('ExternalSenderAdminAddress');
    }

    if (formValue.EnableFileFilter) {
        schema.contains.properties.FileTypeAction = { type: 'string', const: formValue.FileTypeAction };
    }

    if (formValue.FileTypes && formValue.FileTypes.length > 0 && formValue.EnableFileFilter) {
        schema.contains.properties.FileTypes = {
            type: 'array',
            items: {
                type: 'string',
                enum: formValue.FileTypes,
            },
            minItems: formValue.FileTypes.length,
            maxItems: formValue.FileTypes.length,
            uniqueItems: true,
        };

        schema.contains.required.push('FileTypes');
    }

    if (formValue.CustomNotifications) {
        if (formValue.CustomFromName) {
            schema.contains.properties.CustomFromName = { type: 'string', const: formValue.CustomFromName.trim() };
            schema.contains.required.push('CustomFromName');
        }

        if (formValue.CustomFromAddress) {
            schema.contains.properties.CustomFromAddress = {
                type: 'string',
                const: formValue.CustomFromAddress.trim().toLowerCase(),
            };
            schema.contains.required.push('CustomFromAddress');
        }

        if (formValue.EnableInternalSenderAdminNotifications) {
            if (formValue.CustomInternalSubject) {
                schema.contains.properties.CustomInternalSubject = {
                    type: 'string',
                    const: formValue.CustomInternalSubject.trim(),
                };
                schema.contains.required.push('CustomInternalSubject');
            }

            if (formValue.CustomInternalBody) {
                schema.contains.properties.CustomInternalBody = {
                    type: 'string',
                    const: formValue.CustomInternalBody.trim(),
                };
                schema.contains.required.push('CustomInternalBody');
            }
        }

        if (formValue.EnableExternalSenderAdminNotifications) {
            if (formValue.CustomExternalSubject) {
                schema.contains.properties.CustomExternalSubject = {
                    type: 'string',
                    const: formValue.CustomExternalSubject.trim(),
                };
                schema.contains.required.push('CustomExternalSubject');
            }

            if (formValue.CustomExternalBody) {
                schema.contains.properties.CustomExternalBody = {
                    type: 'string',
                    const: formValue.CustomExternalBody.trim(),
                };
                schema.contains.required.push('CustomExternalBody');
            }
        }
    }

    return schema;
}
